import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Icons } from '../common';

/**
 * QCLayout - SuperAdmin-only Quality Control Panel
 * Provides navigation for QC Dashboard, File Upload, Article Viewing, and Analysis History
 * All access restricted to SuperAdmin role
 */
const QCLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    // Verify SuperAdmin access
    const isSuperAdmin = user?.role === 'SuperAdmin';

    if (!isSuperAdmin) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icons.LockClosedIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-6">QC Panel is only available to SuperAdmin users.</p>
                    <button
                        onClick={() => navigate('/')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                    >
                        <Icons.HomeIcon className="w-5 h-5" />
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const menuSections = [
        {
            title: 'QC Dashboard',
            items: [
                {
                    path: '/qc/dashboard',
                    icon: Icons.HomeIcon,
                    label: 'Dashboard',
                    description: 'View QC overview and recent analyses'
                }
            ]
        },
        {
            title: 'QC Operations',
            items: [
                {
                    path: '/qc/upload',
                    icon: Icons.UploadIcon,
                    label: 'Upload File',
                    description: 'Upload document for QC analysis'
                },
                {
                    path: '/qc/articles',
                    icon: Icons.DocumentListIcon,
                    label: 'View Articles',
                    description: 'Browse previously analyzed articles'
                }
            ]
        },
        {
            title: 'System',
            items: [
                {
                    path: '/admin/dashboard',
                    icon: Icons.CogIcon,
                    label: 'Admin Panel',
                    description: 'Return to admin dashboard'
                }
            ]
        }
    ];

    const isActivePath = (path) => {
        return location.pathname === path;
    };

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar */}
            <div
                className={`fixed inset-y-0 left-0 z-50 bg-white shadow-xl border-r border-gray-100 transform transition-all duration-300 ease-in-out lg:static lg:inset-0 ${
                    isSidebarOpen ? 'w-72 translate-x-0' : 'w-16 translate-x-0'
                }`}
            >
                {/* Sidebar Header */}
                <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 via-blue-50 to-indigo-50">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                            <Icons.CheckCircleIcon className="w-5 h-5 text-white" />
                        </div>
                        {isSidebarOpen && (
                            <div className="ml-3">
                                <span className="text-lg font-bold text-gray-900 tracking-tight">QC PANEL</span>
                                <div className="text-xs text-indigo-600 font-semibold">SuperAdmin</div>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                    >
                        {isSidebarOpen ? (
                            <Icons.ChevronLeftIcon className="w-5 h-5" />
                        ) : (
                            <Icons.MenuIcon className="w-5 h-5" />
                        )}
                    </button>
                </div>

                {/* Navigation Menu */}
                <nav className="mt-8 px-2 flex-1 overflow-y-auto">
                    <div className="space-y-6">
                        {menuSections.map((section) => (
                            <div key={section.title}>
                                {isSidebarOpen && (
                                    <div className="px-4 mb-3">
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            {section.title}
                                        </h3>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    {section.items.map((item) => {
                                        const isActive = isActivePath(item.path);
                                        return (
                                            <button
                                                key={item.path}
                                                onClick={() => navigate(item.path)}
                                                className={`w-full group flex items-center ${
                                                    isSidebarOpen ? 'px-4 py-3.5' : 'px-2 py-3 justify-center'
                                                } text-left rounded-xl transition-all duration-200 ${
                                                    isActive
                                                        ? 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-lg shadow-indigo-500/25'
                                                        : 'text-gray-700 hover:bg-gray-50 hover:shadow-md'
                                                }`}
                                                title={!isSidebarOpen ? item.label : ''}
                                            >
                                                <div
                                                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                        isSidebarOpen ? 'mr-3' : ''
                                                    } transition-colors ${
                                                        isActive
                                                            ? 'bg-white/20'
                                                            : 'bg-gray-100 group-hover:bg-gray-200'
                                                    }`}
                                                >
                                                    <item.icon
                                                        className={`w-5 h-5 ${
                                                            isActive
                                                                ? 'text-white'
                                                                : 'text-gray-600 group-hover:text-gray-700'
                                                        }`}
                                                    />
                                                </div>
                                                {isSidebarOpen && (
                                                    <div className="flex-1">
                                                        <div className="text-sm font-semibold">{item.label}</div>
                                                        <div className="text-xs opacity-75">{item.description}</div>
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </nav>

                {/* User Info & Logout */}
                <div className="border-t border-gray-100 p-4">
                    <div
                        className={`flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors ${
                            !isSidebarOpen && 'justify-center'
                        }`}
                    >
                        {isSidebarOpen ? (
                            <>
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center text-white font-semibold">
                                    {user?.username?.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">
                                        {user?.username}
                                    </p>
                                    <p className="text-xs text-gray-500">{user?.role}</p>
                                </div>
                            </>
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center text-white font-semibold">
                                {user?.username?.charAt(0).toUpperCase()}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={handleLogout}
                        className={`w-full mt-3 flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors text-sm font-medium ${
                            !isSidebarOpen && 'justify-center'
                        }`}
                        title="Logout"
                    >
                        <Icons.LogoutIcon className="w-5 h-5" />
                        {isSidebarOpen && 'Logout'}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Top Bar */}
                <div className="sticky top-0 z-40 bg-white border-b border-gray-200/50 shadow-sm lg:hidden">
                    <div className="flex items-center justify-between h-16 px-4">
                        <h1 className="text-lg font-semibold text-gray-900">QC Panel</h1>
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                            <Icons.MenuIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <main className="flex-1 overflow-auto">
                    {children}
                </main>
            </div>

            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}
        </div>
    );
};

export default QCLayout;
